import { Link } from "react-router-dom";
import { Boxes, TrendingUp, PackagePlus, Receipt, Wallet, AlertTriangle, ShoppingCart, HandCoins, Printer, UserCheck, ArrowRight, Truck } from "lucide-react";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Legend } from "recharts";
import { format, parseISO } from "date-fns";
import { PageHeader, KpiCard, SectionCard, Loader, EmptyState } from "../components/common";
import { MovementTable } from "../components/MovementTable";
import { useApi } from "../hooks/useApi";
import { useDepot } from "../context/DepotContext";
import { useAuth } from "../context/AuthContext";
import { TZ, money, qtyFmt } from "../lib/api";

const QUICK_ACTIONS = [
  { to: "/stock-out", key: "sale", label: "Record sale", icon: ShoppingCart, cls: "text-emerald-400" },
  { to: "/stock-in", key: "stock-in", label: "Receive stock", icon: PackagePlus, cls: "text-blue-400" },
  { to: "/expenses", key: "expense", label: "Add expense", icon: Receipt, cls: "text-rose-400" },
  { to: "/debtors", key: "credit", label: "Collect credit", icon: HandCoins, cls: "text-amber-400" },
  { to: "/payables", key: "pay-supplier", label: "Pay supplier", icon: Truck, cls: "text-blue-400" },
  { to: "/report", key: "report", label: "Print today's report", icon: Printer, cls: "text-slate-200" },
];

function QuickActions() {
  return (
    <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3" data-testid="employee-quick-actions">
      {QUICK_ACTIONS.map((a, i) => (
        <Link key={a.key} to={a.to} data-testid={`quick-action-${a.key}`} style={{ animationDelay: `${i * 50}ms` }}
          className="rise group flex items-center gap-3 rounded-lg border border-slate-800 bg-slate-900 px-4 py-4 transition-colors hover:border-amber-500/60">
          <a.icon className={`h-5 w-5 ${a.cls}`} />
          <span className="text-sm font-medium">{a.label}</span>
          <ArrowRight className="ml-auto h-4 w-4 text-slate-600 transition-transform group-hover:translate-x-0.5" />
        </Link>
      ))}
    </div>
  );
}

function AttentionCard({ to, icon: Icon, label, value, sub, testid }) {
  return (
    <Link to={to} data-testid={testid} className="rise flex items-center gap-4 rounded-lg border border-amber-500/30 bg-amber-500/5 px-5 py-4 transition-colors hover:border-amber-500">
      <Icon className="h-6 w-6 text-amber-400" />
      <div>
        <div className="label-mono !text-amber-400">{label}</div>
        <div className="font-mono text-xl font-bold">{value}</div>
        <div className="text-xs text-slate-400">{sub}</div>
      </div>
      <ArrowRight className="ml-auto h-4 w-4 text-amber-400" />
    </Link>
  );
}

function OwnerAttention({ data }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="owner-attention">
      <AttentionCard to="/users" icon={UserCheck} label="Account requests" value={data.pending_users} sub="Waiting for your approval" testid="owner-pending-approvals" />
      <AttentionCard to="/debtors" icon={HandCoins} label="Owed by customers" value={money(data.outstanding_credit)} sub={`${data.debtor_count} customers on credit`} testid="owner-outstanding-credit" />
      <AttentionCard to="/payables" icon={Truck} label="Owed to suppliers" value={money(data.payables.outstanding)} sub={`${data.payables.overdue_count} overdue · ${data.payables.due_soon_count} due soon`} testid="owner-supplier-payables" />
    </div>
  );
}

function SupplierReminder({ p }) {
  if (!p.overdue_count && !p.due_soon_count) return null;
  return (
    <Link to="/payables" data-testid="supplier-reminder-banner"
      className="rise flex items-center gap-3 rounded-lg border border-rose-500/40 bg-rose-500/10 px-5 py-3 text-sm text-rose-200 transition-colors hover:border-rose-400">
      <AlertTriangle className="h-5 w-5 shrink-0 text-rose-400" />
      <span>
        Supplier payment reminder: {p.overdue_count > 0 && <b>{money(p.overdue_total)} overdue ({p.overdue_count})</b>}
        {p.overdue_count > 0 && p.due_soon_count > 0 && " · "}
        {p.due_soon_count > 0 && <b>{money(p.due_soon_total)} due within 3 days ({p.due_soon_count})</b>}
      </span>
      <ArrowRight className="ml-auto h-4 w-4" />
    </Link>
  );
}

function DepotBreakdown({ rows }) {
  const colors = ["#8B5CF6", "#06B6D4", "#F97316"];
  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
      {rows.map((d, i) => (
        <div key={d.depot_id} className="rise rounded-lg border border-slate-800 bg-slate-900 p-5" style={{ animationDelay: `${i * 70}ms` }} data-testid={`depot-breakdown-${d.code}`}>
          <div className="flex items-center gap-2">
            <span className="h-2.5 w-2.5 rounded-full" style={{ background: colors[i % 3] }} />
            <span className="font-display text-lg font-bold uppercase">{d.depot_name}</span>
            {d.low_stock > 0 && <span className="ml-auto text-xs text-amber-400">{d.low_stock} low</span>}
          </div>
          <dl className="mt-4 grid grid-cols-2 gap-3 text-sm">
            <div><dt className="label-mono">Sales today</dt><dd className="font-mono text-emerald-400">{money(d.sales)}</dd></div>
            <div><dt className="label-mono">Expenses</dt><dd className="font-mono text-rose-400">{money(d.expenses + d.purchases)}</dd></div>
            <div><dt className="label-mono">Net</dt><dd className="font-mono">{money(d.net)}</dd></div>
            <div><dt className="label-mono">Stock value</dt><dd className="font-mono text-amber-400">{money(d.stock_value)}</dd></div>
          </dl>
        </div>
      ))}
    </div>
  );
}

const CHART = {
  cursor: { fill: "#0f172a" },
  tooltip: { background: "#0f172a", border: "1px solid #334155", borderRadius: 8 },
  legend: { fontSize: 12 },
  radius: [3, 3, 0, 0],
};

function SalesChart({ series }) {
  const data = series.map((s) => ({ ...s, label: format(parseISO(s.date), "EEE d") }));
  return (
    <div className="h-[280px] p-4" data-testid="sales-chart">
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={data} barGap={4}>
          <CartesianGrid stroke="#1e293b" vertical={false} />
          <XAxis dataKey="label" stroke="#64748b" fontSize={11} tickLine={false} axisLine={false} />
          <YAxis stroke="#64748b" fontSize={11} tickLine={false} axisLine={false} tickFormatter={(v) => `${Number(v).toLocaleString()}`} width={80} />
          <Tooltip cursor={CHART.cursor} contentStyle={CHART.tooltip} formatter={money} />
          <Legend wrapperStyle={CHART.legend} />
          <Bar dataKey="sales" name="Sales (in)" fill="#10B981" radius={CHART.radius} />
          <Bar dataKey="outflow" name="Purchases + expenses (out)" fill="#EF4444" radius={CHART.radius} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

function LowStockList({ items }) {
  if (!items.length) return <EmptyState text="All stock above reorder levels" testid="low-stock-empty" />;
  return (
    <ul className="divide-y divide-slate-800" data-testid="low-stock-list">
      {items.map((i) => (
        <li key={`${i.product_id}-${i.depot_id}`} className="flex items-center justify-between px-5 py-3 text-sm">
          <div className="min-w-0">
            <div className="truncate font-medium">{i.name}</div>
            <div className="font-mono text-[11px] text-slate-500">{i.sku} · {i.depot_name}</div>
          </div>
          <div className="text-right">
            <div className={`font-mono ${i.qty <= 0 ? "text-rose-400" : "text-amber-400"}`}>{qtyFmt(i.qty)} {i.unit}</div>
            <div className="text-[11px] text-slate-500">min {qtyFmt(i.reorder_level)}</div>
          </div>
        </li>
      ))}
    </ul>
  );
}

export default function Dashboard() {
  const { depotParam, scopeLabel } = useDepot();
  const { isAdmin, user } = useAuth();
  const { data } = useApi("/dashboard", { depot_id: depotParam, tz: TZ });
  if (!data) return <Loader />;
  const t = data.today;
  return (
    <div className="space-y-6">
      <PageHeader eyebrow={`${scopeLabel} · ${format(parseISO(data.date), "EEEE, MMM d")}`} title={isAdmin ? "Owner Dashboard" : "My Depot"}
        subtitle={isAdmin ? "Monitor every depot: stock value, sales, spending, credit and cash position." : `Welcome back, ${user.name}. Here's how ${scopeLabel} is doing today.`} />
      {isAdmin ? <OwnerAttention data={data} /> : <QuickActions />}
      <SupplierReminder p={data.payables} />
      <div className="grid grid-cols-2 lg:grid-cols-3 2xl:grid-cols-6 gap-4">
        <KpiCard label="Stock value" value={money(data.stock_value)} sub={`Retail ${money(data.retail_value)}`} icon={Boxes} tone="amber" testid="kpi-total-stock-value" />
        <KpiCard label="Sales today" value={money(t.sales)} sub={`${data.sales_count} sales · ${money(t.credit_sales)} on credit`} icon={TrendingUp} tone="emerald" testid="kpi-today-sales" delay={50} />
        <KpiCard label="Purchases today" value={money(t.purchases)} sub="Stock received" icon={PackagePlus} tone="blue" testid="kpi-today-purchases" delay={100} />
        <KpiCard label="Expenses today" value={money(t.expenses)} sub="Operating costs" icon={Receipt} tone="rose" testid="kpi-today-expenses" delay={150} />
        <KpiCard label="Net cash today" value={money(t.net)} sub="Cash in − cash out" icon={Wallet} tone={t.net >= 0 ? "emerald" : "rose"} testid="kpi-net-cashflow" delay={200} />
        <KpiCard label="Low stock" value={data.low_stock_count} sub={`${data.product_count} products`} icon={AlertTriangle} tone="slate" testid="kpi-low-stock-count" delay={250} />
      </div>
      {data.breakdown.length > 0 && <DepotBreakdown rows={data.breakdown} />}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        <SectionCard title="Last 7 days" className="lg:col-span-8"><SalesChart series={data.series} /></SectionCard>
        <SectionCard title="Low stock" className="lg:col-span-4"
          right={<Link to="/products" className="text-xs text-amber-400 hover:underline" data-testid="view-products-link">View all</Link>}>
          <LowStockList items={data.low_stock} />
        </SectionCard>
      </div>
      <SectionCard title="Recent activity" testid="recent-activity">
        <MovementTable rows={data.recent} showType testid="recent-activity-table" />
      </SectionCard>
    </div>
  );
}
